import cn, { Argument as ClassName } from 'classnames'
import React, { memo, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'

import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import useKeyboardHandle from '../../../common/hooks/useKeyboardHandle'
import {
    ExternalServer,
    Partner,
    useExternalServerDeleteMutation,
    useExternalServerGetChargePointsQuery,
    useExternalServerGetPartnersQuery,
    useExternalServerUpdatePartnerConnectionsMutation,
} from '../../../common/redux/api/web-api'
import useText from '../../../common/hooks/useText'
import BootstrapModalLayout from '../../../ui/BootstrapModalLayout'
import Text from '../../../ui/Text'
import { CloseIcon } from '../../../common/icons/close-icon/CloseIcon'
import { GoToIcon } from '../../../lib/icons/goto-icon'
import InlineAlert, { AlertType } from '../../../common/components/InlineAlert'

export type DeleteExternalServerModalProps = {
    closeModal: () => void
    externalServer?: ExternalServer
    className?: ClassName
    style?: React.CSSProperties
}

const ModalSubHeading = styled.div`
    font-weight: 300;
    font-size: 14px;
    color: ${({ theme }) => theme.font.midDarkColor};
    margin: 4px 0px 4px 0px;
`
export const ModalSubText = styled.div`
    font-weight: 400;
    font-size: 16px;
    color: ${({ theme }) => theme.font.darkestColor};
    margin-bottom: 8px;
`
export const ModalSubTextList = styled.div`
    font-weight: 400;
    font-size: 16px;
    color: ${({ theme }) => theme.font.darkestColor};
    margin-bottom: 4px;
    display: flex;
    gap: 4px;
    align-items: center;
    a {
        padding: 4px;
        &:hover {
            cursor: pointer;
            background-color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
        }
        &:focus {
            background-color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
        }
    }

    button {
        background: transparent;
        padding: 4px;
        border: none;
        &:hover {
            cursor: pointer;
            background-color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
        }
        &:focus {
            background-color: ${({ theme }) => theme.brandColors.CA_Light_Gray};
        }
    }
`

export const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow-x: hidden;
`

export const ModalListContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
`

export const ModalListItem = styled.a`
    & {
        color: ${({ theme }) => theme.font.darkestColor};
        text-decoration: underline;
        font-size: 14px;
        margin: 4px;
    }

    &:hover {
        color: ${({ theme }) => theme.font.successColor};
        cursor: pointer;
    }

    &:active {
        color: ${({ theme }) => theme.font.midDarkColor};
    }
`

export const DeleteExternalServerModal = memo(function DeleteExternalServerModal({
    className,
    externalServer,
    closeModal,
    style,
}: DeleteExternalServerModalProps): React.ReactElement {
    const text = useText()
    const [deleteExternalServer] = useExternalServerDeleteMutation()
    const externalServerId = (externalServer && externalServer.id) ?? 0
    const { data: getPartners } = useExternalServerGetPartnersQuery({ externalServerId: externalServer?.id })
    const [addPartner] = useExternalServerUpdatePartnerConnectionsMutation({})

    const [partners, setPartners] = useState<Partner[]>([])

    useEffect(() => setPartners(getPartners), [getPartners])

    const {
        data: chargePoints,
        isFetching: dataLoading,
        isLoading,
    } = useExternalServerGetChargePointsQuery(
        { externalServerId },
        { refetchOnFocus: true, refetchOnMountOrArgChange: true }
    )

    const disabledYesButton =
        (chargePoints && chargePoints?.length > 0) ||
        (partners && partners?.length > 0) ||
        (chargePoints && chargePoints?.length > 0 && partners && partners?.length == 0)
    /* Delete server */

    const deleteServer = async () => {
        if (externalServerId) {
            try {
                await addPartner({
                    externalServerId: externalServer?.id,
                    body: partners,
                }).unwrap()
                await deleteExternalServer({ externalServerId }).unwrap()
                notifySuccess(text('lblServerDeleted'))
                closeModal()
            } catch (e) {
                closeModal()
                notifyError(text('lblServerDeleteError'))
            }
        }
    }

    /* ----- */

    useKeyboardHandle({
        onEnterPress: () => {
            if (!chargePoints || chargePoints?.length === 0) deleteServer()
        },
        onEscapePress: closeModal,
    })

    const handleRemove = (valueToRemove: number | string) => {
        const tempPartners = partners?.filter((partner) => partner?.id !== valueToRemove)
        setPartners(tempPartners)
    }

    return (
        <BootstrapModalLayout
            className={cn(className)}
            style={style}
            title={text('lblServerDeleteModal')}
            titleClassName="modal-header text-center"
            size={'lg'}
            data-testid="delete-modal"
            actions={[
                {
                    btn: 'btn-secondary',
                    callback: closeModal,
                    label: text('btnCancel'),
                },
                {
                    callback: deleteServer,
                    disabled: disabledYesButton,
                    label: text('btnYes'),
                },
            ]}
        >
            {!isLoading && (
                <>
                    <ModalContent data-testid="cpms-name-url">
                        <ModalSubHeading data-testid="cpms-name-heading">
                            <Text code="lblCpmsName"></Text>
                        </ModalSubHeading>
                        <ModalSubText data-testid="cpms-name-text">{externalServer?.description}</ModalSubText>
                        <ModalSubHeading data-testid="cpms-url-heading">
                            <Text code="lblCpmsUrl"></Text>
                        </ModalSubHeading>
                        <ModalSubText data-testid="cpms-url-text">{externalServer?.url}</ModalSubText>
                    </ModalContent>

                    {chargePoints && chargePoints?.length > 0 && (
                        <ModalContent data-testid="chargers-list-to-server">
                            <InlineAlert
                                alertType={AlertType.Warning}
                                data-testid="chargers-list-to-server-text"
                                text={text('lblChargersConnectedToSever')}
                            />
                            <ModalSubHeading data-testid="chargers-list">
                                <Text code="lblChargersffected"></Text>

                                {dataLoading && (
                                    <span data-testid="chargers-list-loading" className="fa fa-spinner fa-pulse"></span>
                                )}
                            </ModalSubHeading>
                            <ModalListContainer>
                                {chargePoints?.map((chargePoint) => (
                                    <ModalSubTextList key={chargePoint}>
                                        <div className="margin-top-2">{chargePoint}</div>

                                        <Link
                                            to={`/chargepoints/${chargePoint}`}
                                            data-testid={`link-charge-point-${chargePoint}`}
                                            target="_blank"
                                        >
                                            <GoToIcon size={13} />
                                        </Link>
                                    </ModalSubTextList>
                                ))}
                            </ModalListContainer>
                        </ModalContent>
                    )}

                    {partners && partners?.length > 0 && (
                        <>
                            <ModalContent data-testid="partners-list-to-server">
                                <InlineAlert
                                    data-testid="PartnersConnectedToServer"
                                    alertType={AlertType.Warning}
                                    text={text('lblPartnersConnectedToServer')}
                                />
                                <ModalSubHeading data-testid="cpms-partner-heading">
                                    <Text code="lblAssignedPartners" />
                                </ModalSubHeading>

                                <ModalListContainer>
                                    {partners?.map((partner) => (
                                        <>
                                            <ModalSubTextList data-testid="cpms-assigned-partner">
                                                {partner?.name}
                                                <button
                                                    className="margin-top-2"
                                                    data-testid="btn-remove-partner"
                                                    onClick={() => {
                                                        handleRemove(partner?.id)
                                                    }}
                                                >
                                                    <CloseIcon size={13} />
                                                </button>
                                            </ModalSubTextList>
                                        </>
                                    ))}
                                </ModalListContainer>
                            </ModalContent>
                        </>
                    )}
                </>
            )}
        </BootstrapModalLayout>
    )
})
